import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { useFilters } from '@/context/filters-context';
import { FilterableAttribute } from '@/types';

export const AttributesFilter = () => {
    const { filters, setFilters, filterConfig } = useFilters();

    const setAttr = (key: string, value: string[] | [number, number] | boolean | null) => {
        setFilters((prev) => {
            const attrs = { ...prev.attrs };
            if (
                value === null ||
                value === false ||
                (Array.isArray(value) && value.length === 0)
            ) {
                delete attrs[key];
            } else {
                attrs[key] = value;
            }
            return { ...prev, attrs };
        });
    };

    if (!filterConfig.attributes.length) {
        return null;
    }

    return (
        <div className="space-y-6">
            {filterConfig.attributes.map((attr) => (
                <AttributeField key={attr.key} attr={attr} setAttr={setAttr} filters={filters} />
            ))}
        </div>
    );
};

const AttributeField = ({
    attr,
    setAttr,
    filters,
}: {
    attr: FilterableAttribute;
    setAttr: (key: string, value: string[] | [number, number] | boolean | null) => void;
    filters: ReturnType<typeof useFilters>['filters'];
}) => {
    const { facets } = useFilters();
    const attrFacet = facets.attrs[attr.key];

    if (attr.type === 'number' && attr.range) {
        const facetRange = attrFacet && 'min' in attrFacet ? attrFacet as { min: number; max: number } : null;
        const validFacet = facetRange && facetRange.max > facetRange.min;
        const rangeMin = validFacet ? facetRange.min : attr.range.min;
        const rangeMax = validFacet ? facetRange.max : attr.range.max;

        const current = (filters.attrs[attr.key] as [number, number] | undefined) ?? [rangeMin, rangeMax];

        return (
            <div>
                <Label className="font-medium">{attr.label}</Label>
                <div className="mt-1 flex items-center justify-between text-sm text-muted-foreground">
                    <span>{current[0]}{attr.suffix}</span>
                    <span>{current[1]}{attr.suffix}</span>
                </div>
                <Slider
                    min={rangeMin}
                    max={rangeMax}
                    step={1}
                    value={current}
                    onValueChange={(v) => {
                        const isDefault = v[0] === rangeMin && v[1] === rangeMax;
                        setAttr(attr.key, isDefault ? null : (v as [number, number]));
                    }}
                    className="mt-2 py-4"
                />
            </div>
        );
    }

    if (attr.type === 'select' && attr.options?.length) {
        const selected = (filters.attrs[attr.key] as string[]) ?? [];
        const counts = attrFacet && !('min' in attrFacet) ? attrFacet as Record<string, number> : null;

        // Hide options with zero profiles when facets are available.
        const visibleOptions = counts
            ? attr.options.filter((opt) => (counts[opt.key] ?? 0) > 0)
            : attr.options;

        if (!visibleOptions.length) {
            return null;
        }

        return (
            <div>
                <Label className="mb-2 block font-medium">{attr.label}</Label>
                <div className="grid grid-cols-2 gap-2">
                    {visibleOptions.map((opt) => (
                        <div key={opt.key} className="flex items-center space-x-2">
                            <Checkbox
                                id={`attr-${attr.key}-${opt.key}`}
                                checked={selected.includes(opt.key)}
                                onCheckedChange={(checked) => {
                                    const next = checked
                                        ? [...selected, opt.key]
                                        : selected.filter((v) => v !== opt.key);
                                    setAttr(attr.key, next);
                                }}
                            />
                            <Label htmlFor={`attr-${attr.key}-${opt.key}`} className="text-sm">
                                {opt.label}
                                {counts?.[opt.key] !== undefined && (
                                    <span className="ml-1 text-muted-foreground">({counts[opt.key]})</span>
                                )}
                            </Label>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (attr.type === 'boolean') {
        const checked = (filters.attrs[attr.key] as boolean) ?? false;

        return (
            <div className="flex items-center space-x-2">
                <Checkbox
                    id={`attr-${attr.key}`}
                    checked={checked}
                    onCheckedChange={(value) => setAttr(attr.key, value as boolean)}
                />
                <Label htmlFor={`attr-${attr.key}`}>{attr.label}</Label>
            </div>
        );
    }

    return null;
};
